import { useState, useEffect, useCallback } from 'react';
import {
  api,
  CreditBalance,
  CreditTransactionGroup,
  CreditTransactionGroupsResult,
  SpendingCategorySummary,
} from '../../lib/api';

interface UseCreditsReturn {
  balance: CreditBalance | null;
  spendingSummary: SpendingCategorySummary[];
  /**
   * Server-grouped charge groups (view=grouped): each reservation and its
   * finalize/cancel refunds arrive as ONE group with a net amount, so the
   * UI never shows a scary raw reservation row without its refund.
   */
  transactionGroups: CreditTransactionGroup[];
  /** Total count of GROUPS on the server (pagination unit). */
  transactionsTotal: number;
  loading: boolean;
  loadingTransactions: boolean;
  error: string | null;
  refresh: () => void;
  loadMoreTransactions: () => void;
  hasMoreTransactions: boolean;
}

export function useCredits(): UseCreditsReturn {
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [spendingSummary, setSpendingSummary] = useState<SpendingCategorySummary[]>([]);
  const [transactionGroups, setTransactionGroups] = useState<CreditTransactionGroup[]>([]);
  const [transactionsTotal, setTransactionsTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  // Match the server's MAX_LIMIT (see services/api-service/src/routes/credits/user.ts).
  // Grouping happens server-side (a group never splits across pages), so the
  // large page is purely about reducing Load More clicks.
  const limit = 100;

  const fetchBalance = useCallback(async () => {
    try {
      const result = await api.getCreditBalance();
      setBalance(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load credit balance');
    }
  }, []);

  const fetchSummary = useCallback(async () => {
    try {
      const result = await api.getCreditSpendingSummary();
      setSpendingSummary(result.categories);
    } catch {
      // Non-critical — summary failure shouldn't block the page
    }
  }, []);

  const fetchTransactions = useCallback(async (newOffset: number = 0, append: boolean = false) => {
    setLoadingTransactions(true);
    try {
      const result: CreditTransactionGroupsResult = await api.getCreditTransactionGroups({
        limit,
        offset: newOffset,
      });
      if (append) {
        setTransactionGroups((prev) => [...prev, ...result.groups]);
      } else {
        setTransactionGroups(result.groups);
      }
      setTransactionsTotal(result.total);
      setOffset(newOffset);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load transactions');
    } finally {
      setLoadingTransactions(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    await Promise.all([fetchBalance(), fetchSummary(), fetchTransactions(0, false)]);
    setLoading(false);
  }, [fetchBalance, fetchSummary, fetchTransactions]);

  const loadMoreTransactions = useCallback(() => {
    if (!loadingTransactions && offset + limit < transactionsTotal) {
      fetchTransactions(offset + limit, true);
    }
  }, [fetchTransactions, loadingTransactions, offset, transactionsTotal]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return {
    balance,
    spendingSummary,
    transactionGroups,
    transactionsTotal,
    loading,
    loadingTransactions,
    error,
    refresh,
    loadMoreTransactions,
    hasMoreTransactions: offset + limit < transactionsTotal,
  };
}
